import { EffectRef, Injectable, OnDestroy, computed, effect, inject } from '@angular/core';
import { IAddressFormat, IAddressModel, ICustomer, IZipCodeModel } from '@partners/api';
import { CatalogQuery, CatalogService, TCatalogOption, TResidenceOption } from '@partners/catalog';
import { HttpService, IHttpOptions, filterResult } from '@partners/http';
import { AddressStore, AuthLoginStore } from '@partners/state';
import { filterObjects, isEmpty, isNotEmpty, takeOnce, toUrlParams } from '@partners/utils';
import { Observable, distinctUntilChanged, map, of, startWith, switchMap } from 'rxjs';
import { ContextBuilder } from '../../http';

@Injectable({
  providedIn: 'root'
})
export class AddressFacade implements OnDestroy {
  private _catalogService = inject(CatalogService);
  private _httpService = inject(HttpService);
  private _catalogQuery = inject(CatalogQuery);
  private _addressStore = inject(AddressStore);
  private _loginStore = inject(AuthLoginStore);
  private _effectRef: EffectRef;

  readonly addressFormat$$ = computed(() => {
    const addressModel: IAddressModel = this._addressStore.model$$();
    if (!addressModel) {
      return undefined;
    }

    const { houseNumber, entrance, appartmentNum, zipCode, zipCode7, residenceName, streetName, residenceCode } =
      addressModel;

    const addressFormat: IAddressFormat = {
      appartmentNum,
      houseNumber,
      entrance,
      residenceName,
      streetName,
      zipCode: zipCode7 || zipCode
    };
    return addressFormat;
  });
  readonly addressModel$$ = computed(() => this._addressStore.model$$());

  /**
   * @description
   * when user successfully logs in, we get his full data back from the server, including address,
   * bank & debits etc'. for each of those domains, we need to copy their data from the auth store
   * into their own stores. this is due to stupid server architecture
   */
  readonly authCustomerData = effect(
    () => {
      const customerData = this._loginStore.customerData$$();

      if (!customerData) {
        return;
      }

      this.updateAddressModel(customerData);

      this.authCustomerData.destroy();
    },
    { allowSignalWrites: true }
  );

  constructor() {}

  hasValidAddress$$ = computed<boolean>(() => {
    
    const addressModel: IAddressModel = this._addressStore.model$$();
    if (!addressModel) {
      return undefined;
    }
    const { residenceCode, streetCode, houseNumber } = addressModel;

    const residence: TResidenceOption = this.getResidenceByCode(residenceCode);

    if (!residence) {
      return false;
    }

    const { isContainSt } = residence;

    if (isContainSt) {
      return isNotEmpty(residenceCode) && isNotEmpty(streetCode) && isNotEmpty(houseNumber);
    }

    return isNotEmpty(residenceCode) && isNotEmpty(houseNumber);
  });

  ngOnDestroy(): void {
    this._effectRef && this._effectRef.destroy();
  }

  getResidenceByCode(residenceCode: number): TResidenceOption {
    return this._catalogQuery.findResidence(residenceCode);
  }

  filterStreets(streetQuery: string, residenceCode: string | number): Observable<TCatalogOption[]> {
    const context = new ContextBuilder().disableLoader().addUrlModifiers({ omitProcess: true }).build();
    return this._catalogQuery.filterStreetsByName$(streetQuery, residenceCode, { context }).pipe(
      map((streets) => {
        return filterObjects<TCatalogOption>(streets, '', {
          props: ['key'],
          maxResults: 50
        });
      })
    );
  }

  filterResidences(residenceQuery: string = null): TResidenceOption[] {
    const residenceList: TResidenceOption[] = this._catalogService.fromResidence();
    if (!residenceQuery) {
      return residenceList;
    }

    const filteredList = filterObjects<TResidenceOption>(residenceList, residenceQuery, {
      props: ['key'],
      maxResults: 50
    });

    return filteredList;
  }

  findResidence(residenceCode: number): TResidenceOption | undefined {
    const residenceList: TResidenceOption[] = this._catalogService.fromResidence();

    const residenceOption = residenceList.find(({ value }) => value === residenceCode);

    return residenceOption;
  }

  getZipCode(residenceCode: number, street: number | null, houseNumber: number): Observable<IZipCodeModel> {
    let zipCodeQuery = 'catalog/zipcode';
    zipCodeQuery += toUrlParams([residenceCode, street, houseNumber], { includeNulls: true });

    const context = new ContextBuilder().addUrlModifiers({ omitProcess: true }).disableLoader().build();
    const options: IHttpOptions = { context };

    return of(zipCodeQuery).pipe(
      takeOnce(),
      startWith(null),
      distinctUntilChanged(),
      switchMap(() => {
        return this._httpService.fetch<null, IZipCodeModel>(zipCodeQuery, options);
      }),
      filterResult()
    );
  }

  updateResidenceName(residenceName: string) {
    let model = this._addressStore.model$$();
    isEmpty(model) && (model = this.createEmptyAddressModel());
    model = { ...model, residenceName };
    this._addressStore.dispatchUpdateModel({ model });
  }

  updateStreetName(streetName: string) {
    let model = this._addressStore.model$$();
    isEmpty(model) && (model = this.createEmptyAddressModel());
    model = { ...model, streetName };
    this._addressStore.dispatchUpdateModel({ model });
  }

  createEmptyAddressModel(): IAddressModel {
    return {
      residenceCode: null,
      streetCode: null,
      zipCode: null,
      appartmentNum: null,
      entrance: null,
      streetName: null,
      residenceName: null,
      houseNumber: null,
      email: null
    } as IAddressModel;
  }

  private updateAddressModel({
    residence,
    residenceName,
    street,
    streetName,
    houseNumber,
    zipCode,
    entrance,
    appartmentNum,
    email
  }: ICustomer) {
    const addressModel: IAddressModel = {
      residenceCode: residence,
      streetCode: street,
      zipCode,
      appartmentNum,
      entrance,
      streetName,
      residenceName,
      houseNumber,
      email
    };

    addressModel.zipCode7 = zipCode;

    /** update the address store */
    this._addressStore.dispatchUpdateModel({ model: addressModel });
  }
}
